import { AfterViewInit, ChangeDetectionStrategy, Component, effect, ElementRef, input, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { debounceTime, map, Observable } from 'rxjs';
import { AuthenticatedPageComponent } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { Channel, Message } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { InboxService } from 'src/app/services/inbox.service';
import { MailboxComponent } from '../mailbox.component';

@Component({
    selector: 'app-conversation',
    templateUrl: './conversation.component.html',
    styleUrls: ['./conversation.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConversationComponent extends AuthenticatedPageComponent implements OnInit, AfterViewInit {

    public static route = 'mailbox/conversation';

    // Aliased to the route param name ('channelId') while keeping a distinct property name, since
    // channelId below is a plain mutable field (parsed from this input, then read/written
    // elsewhere) rather than something this input() could be directly.
    channelIdParam = input<string>(undefined, { alias: 'channelId' });

    channelId: number;
    // Reassigned from updateActiveChannel(), invoked from the channels$ subscribe below and from
    // the constructor's effect() - never a template-bound handler - so this needs to be a signal
    // for OnPush to see updates (both a channel switch and a live message arriving in the
    // currently-open channel).
    protected activeChannel = signal<Channel>(null);
    composeMessage = '';

    @ViewChild('chatDisplay', { static: false }) scrollingContainer: ElementRef;

    channels: Observable<Channel[]>;

    constructor(private inboxService: InboxService, coreAppService: ApplicationService, router: Router) {
        super(router, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
        this.channels = this.inboxService.getObservableChannels();
        this.subscribe(this.channels, this.updateActiveChannel.bind(this));

        effect((onCleanup) => {
            this.channelId = Number.parseInt(this.channelIdParam());
            // this.inboxService.getMessagesBatch(this.channelId);
            if (!this.updateActiveChannel()) { //if we don't have this channel loaded, go back to mailbox
                const link = [`/${MailboxComponent.route}`];
                this.router.navigate(link);
            } else {
                // Ongoing for as long as this conversation stays open - re-runs on every channels$
                // emission (the initial snapshot via shareReplay(1), plus every later live push),
                // not just once on mount. See markVisibleMessagesRead for why this is deliberately
                // not also gated on document visibility/focus.
                //
                // Disposed via onCleanup, not the tracked this.subscribe() helper: this effect
                // itself re-runs on every channelIdParam change (switching conversations under this
                // app's route reuse strategy), and this.subscribe() only tears down at component
                // destroy - without onCleanup, each conversation switch would leave the previous
                // conversation's read-marking subscription running alongside the new one.
                const markReadSub = this.channels.pipe(
                    map(() => this.inboxService.allChannelsMapSignal().get(this.channelId)?.()),
                    debounceTime(800))
                    .subscribe(channel => this.markVisibleMessagesRead(channel));
                onCleanup(() => markReadSub.unsubscribe());
            };
        });
    }

    updateActiveChannel(): boolean {
        if (this.channelId !== null) {
            this.activeChannel.set(this.inboxService.allChannelsMapSignal().get(this.channelId)?.());
            return true;
        }
        return false;
    }

    // Marks whatever is currently unread in `channel` as read. Deliberately NOT additionally
    // gated on document.visibilityState/window focus: that would stop a backgrounded tab from
    // false-marking messages read on arrival, but the Page Visibility API is unreliable enough
    // across real browsers/devices (mobile Safari backgrounding, some Android WebViews - the
    // class of bug behind WhatsApp/etc. sometimes leaving a conversation you already replied to
    // stuck "unread") that it was considered and rejected here for now. The trade is: this can
    // mark a message read that arrived while the tab was backgrounded but the route stayed
    // mounted. Revisit only with a more robust signal than raw Page Visibility if that becomes a
    // real complaint - see Docs/CritterClient/Issues-And-Bugs.md ARCH-12.
    private markVisibleMessagesRead(channel: Channel): void {
        if (!channel) return;
        this.inboxService.markNotificationsRead(...channel.Messages
            .filter(m => m.targetingNotification && !m.targetingNotification.ReadDate));
        this.inboxService.markMessagesRead(...channel.Messages
            .filter(m => !m.Read));
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            // #chatDisplay (and so this ViewChild) only exists once activeChannel is set, which -
            // now that the channel id arrives via a signal input() rather than a synchronous
            // route.params subscribe - is no longer guaranteed to happen before this component's
            // first view init.
            if (!this.scrollingContainer) { return; }
            let alertedItems = this.scrollingContainer.nativeElement.getElementsByClassName('alerted-item');
            if (alertedItems?.length) {
                alertedItems[alertedItems.length - 1]?.scrollIntoView({ behavior: 'smooth' });
            }
        }, 700);
    }

    sendMessage(messageText: string, replyMessage: number | null): void {
        const message = new Message(null, null);
        message.ChannelId = this.activeChannel().Id;
        message.SendDate = new Date(Date.now());
        message.MessageText = messageText;
        message.IsMyMessage = true;
        if (replyMessage) {
            message.ParentMessageId = replyMessage;
        }
        this.inboxService.sendMessage(message);
        this.composeMessage = '';
    }

    ngOnInit(): void {
    }
}
